import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Header } from '~/app/components-nds/common/header'
import { Footer } from '~/app/components-nds/common/footer'
import CartSelectBar from '~/app/components-nds/domestic/cart/cart-select-bar'
import CartProductSection from '~/app/components-nds/domestic/cart/cart-product-section'
import CartBannerGuide from '~/app/components-nds/domestic/cart/cart-banner-guide'
import { useDomesticCartQuery } from '~/app/react-query/domestic/cart'
import { cn, EmptyState, Gototop } from '@yanolja-rnd/nol-design-system'
import { IcCartH72, IcErrorH72 } from '@yanolja-rnd/nol-icons'
import useGoHome from '~/app/utils/useGoHome'
import { useLoading } from '~/app/components/common/LoadingProvider'
import { DomesticCartConverterResponse } from '~/api/routes/domestic/cart/controller/type'
import OrderSummaryCard from '~/app/components-nds/domestic/cart/order-summary-card'
import PurchaseCTAButton from '~/app/components-nds/domestic/cart/purchase-cta-button'
import useIsPcWidth from '~/app/utils/useIsPcWidth'
import CartLogin from '~/app/components-nds/domestic/cart/cart-login'
import useDomesticCartDiscount from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-discount'
import useDomesticCartPrice from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-price'
import useDomesticCartOptions from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-options'
import useVisibilityChange from '~/app/utils/useVisibilityChange'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { isNative } from '~/app/utils/typeguards'

/**
 * New Cart Page Component
 * description: 새로운 장바구니 페이지 (신 버전)
 */

interface CartPageProps {
  viewer: any
}

const DomesticCart = ({ data }: { data: DomesticCartConverterResponse }) => {
  const { hasOptions } = useDomesticCartOptions()

  const mobilePurchaseCtaRef = useRef<HTMLDivElement>(null)
  const pcPurchaseCtaRef = useRef<HTMLDivElement>(null)
  // GoToTop 버튼 위치
  const [goToTopBottom, setGoToTopBottom] = useState(0)

  const { goHome } = useGoHome()
  const { isPcWidth } = useIsPcWidth()

  // 쿠폰 할인
  const discountInfo = useDomesticCartDiscount({
    maxDiscountLimit: data.maxDiscountLimit,
    isMember: data.isMember,
  })
  // 총 상품 가격
  const { totalPrice } = useDomesticCartPrice()
  // 결제 예상 금액
  const expectedPayAmount = useMemo(() => {
    return totalPrice - discountInfo.maxDiscountAmount
  }, [totalPrice, discountInfo.maxDiscountAmount])

  useLayoutEffect(() => {
    // 구매하기 버튼 영역 높이가 동적이라 GoToTop 버튼 위치를 위해 계산
    const purchaseCta = isPcWidth ? pcPurchaseCtaRef.current : mobilePurchaseCtaRef.current
    if (purchaseCta) {
      const purchaseCtaHeight = purchaseCta.offsetHeight
      // padding 포함
      const gotoTopBottom = isPcWidth ? purchaseCtaHeight + 8 : purchaseCtaHeight + 16
      setGoToTopBottom(gotoTopBottom)
    }
  }, [data, isPcWidth, totalPrice])

  return (
    <div className="mx-auto grid w-full grid-cols-[1fr] pc:mt-28 pc:min-h-[calc(100vh-100px-28px)] pc:w-pc-size pc:grid-cols-[880px_360px] pc:grid-rows-[1fr_191px] pc:gap-x-40 pc:gap-y-28">
      {hasOptions ? (
        <div>
          <CartSelectBar />

          <div className="mx-auto max-w-[880px] px-16 py-20 pc:my-16 pc:p-0">
            <CartProductSection {...data.productSection} />
          </div>

          {/* 모바일: 예약 금액, 배너+가이드, 구매하기 버튼  */}
          <div className="flex w-full flex-col gap-16 bg-static-white px-16 py-24 pc:hidden">
            <OrderSummaryCard
              {...discountInfo}
              totalPrice={totalPrice}
              expectedPayAmount={expectedPayAmount}
              isNolCardLinked={data.viewer.isNolCardLinked}
            />
            <CartBannerGuide {...data.benefitBanner} />
            <PurchaseCTAButton
              ref={mobilePurchaseCtaRef}
              isMember={data.isMember}
              viewer={data.viewer}
              expectedPayAmount={expectedPayAmount}
              className={
                'fixed bottom-0 left-0 right-0 mx-auto max-w-mobile-size shrink-0 bg-static-white py-16 shadow-[0_0_8px_0_rgba(0,0,0,0.10)] z-high pc:max-w-none'
              }
            />
          </div>

          {/* PC: 배너+가이드 */}
          <div className={'hidden bg-static-white pc:block pc:gap-20 pc:rounded-16 pc:p-24'}>
            <CartBannerGuide {...data.benefitBanner} />
          </div>
        </div>
      ) : (
        <div className="flex h-[calc(100vh-52px)] w-full flex-col items-center justify-center bg-static-white pc:h-auto pc:bg-transparent">
          <EmptyState
            title="아직 장바구니가 비어있어요"
            description="원하는 상품을 담아두면 한 번에 예약할 수 있어요."
            icon={IcCartH72}
            buttons={[{ content: '담으러 가기', onClick: goHome }]}
          />
          {!data.isMember && <CartLogin className="fixed bottom-0 px-16 py-12 pc:hidden" />}
        </div>
      )}

      <Gototop style={{ bottom: isPcWidth ? '24px' : `${goToTopBottom}px` }} />

      {/* PC: 예약 금액, 구매하기 버튼 */}
      <div className="hidden pc:block pc:h-full">
        <div className={'pc:sticky pc:top-128 pc:flex pc:flex-col pc:gap-y-16'}>
          {!!data.productSection.cartItems.length && (
            <OrderSummaryCard
              className={'px-24 py-16 pc:rounded-16'}
              {...discountInfo}
              totalPrice={totalPrice}
              expectedPayAmount={expectedPayAmount}
              isNolCardLinked={data.viewer.isNolCardLinked}
            />
          )}
          <PurchaseCTAButton
            ref={pcPurchaseCtaRef}
            isMember={data.isMember}
            viewer={data.viewer}
            expectedPayAmount={expectedPayAmount}
            className={'py-24 pc:rounded-16'}
          />
        </div>
      </div>

      <Footer
        className={cn('col-start-1 col-end-3 justify-start', !hasOptions && 'hidden pc:block')}
      />
    </div>
  )
}

/**
 * New Cart Page Component
 * description: 새로운 장바구니 페이지 (신 버전)
 */
const CartPage: React.FC<CartPageProps> = ({ viewer: _viewer }) => {
  const { data, isInitialLoading, isSuccess, refetch } = useDomesticCartQuery()
  const { totalOptionCount } = useDomesticCartOptions()

  useLoading(isInitialLoading)

  const isEnabledNative = useMemo(() => {
    if (typeof window !== 'undefined') {
      const yaNative = getYaNative()
      return isNative(yaNative)
    }
    return false
  }, [])

  useVisibilityChange({
    isActive: isEnabledNative,
    onVisible: refetch,
  })

  return (
    <div className="mx-auto min-h-screen max-w-mobile-size bg-fill-neutral-weak pc:min-w-pc-with-swiper-btn pc:max-w-none">
      <Header cartCount={totalOptionCount} />

      {!isInitialLoading &&
        (isSuccess ? (
          <DomesticCart data={data} />
        ) : (
          <div className="mx-auto flex h-[calc(100vh-52px)] w-full items-center justify-center bg-static-white pc:h-[calc(100vh-100px)] pc:w-pc-size">
            <EmptyState
              title="화면을 불러오지 못했어요"
              icon={IcErrorH72}
              buttons={[{ content: '다시 시도하기', onClick: () => refetch() }]}
            />
          </div>
        ))}
    </div>
  )
}

export default CartPage
